import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthGuard } from '../hooks/useAuth';
import MessagePage from './MessagePage';
import SideMenuLayout from '../components/layout/SideMenuLayout';
import Button from '../components/common/Button';
import ListControls from '../components/common/ListControls';
import AdminCompetitionModal from './AdminCompetitionModal';
import AdminAddHostModal from './AdminAddHostModal';
import AdminAddUsersModal from './AdminAddUsersModal';
import AdminPairPoolModal from './AdminPairPoolModal';
import NotificationComposerModal from './NotificationComposerModal';
import Toast from '../components/common/Toast';
import { AdminUsersListResponse, AdminUserRow, Competition } from '../types/interfaces';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import AdminNav, { AdminSection, toAdminSection } from './admin/AdminNav';
import AgentSection from './admin/agent/AgentSection';
import './Admin.css';

const Admin: React.FC = () => {
  const { canAccess, isLoading } = useAuthGuard({ roles: ['admin']});
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [addHostOpen, setAddHostOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);

  // Evaluations section
  const [evalCompetitionId, setEvalCompetitionId] = useState<number>(0);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState('');
  const [evalToast, setEvalToast] = useState('');
  const [pairPoolModalOpen, setPairPoolModalOpen] = useState(false);

  // Users section
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize] = useState(25);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersSearch, setUsersSearch] = useState('');

  const [addUsersOpen, setAddUsersOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = toAdminSection(searchParams.get('section'));
  const [manageCompetitionId, setManageCompetitionId] = useState<number>(0);

  const loadCompetitions = useCallback(async () => {
    try {
      setCompetitionsLoading(true);
      setCompetitionsError('');
      const token = authUtils.getStoredToken();
      const response = await apiService.post('/competitions/list', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompetitions(response.data || []);
    } catch (err: any) {
      console.error('Error loading competitions (admin):', err);
      setCompetitionsError('Failed to load competitions');
    } finally {
      setCompetitionsLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async (page: number, q?: string) => {
    try {
      setUsersLoading(true);
      setUsersError('');
      const token = authUtils.getStoredToken();
      const response = await apiService.get('/users/list', {
        params: { page, page_size: usersPageSize, q: (q ?? usersSearch).trim() || undefined },
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (response.data || {}) as AdminUsersListResponse;
      setUsers(data.users || []);
      setUsersTotal(data.total || 0);
      setUsersPage(data.page || page);
    } catch (err: any) {
      console.error('Error loading users (admin):', err);
      setUsersError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [usersPageSize, usersSearch]);

  // Important: hooks must not be conditional. We only *execute* the load when admin access is confirmed.
  useEffect(() => {
    if (!isLoading && canAccess) {
      loadCompetitions();
      loadUsers(1);
    }
  }, [isLoading, canAccess, loadCompetitions, loadUsers]);

  // Reload users when search changes (debounced)
  useEffect(() => {
    if (isLoading || !canAccess) return;

    const t = window.setTimeout(() => {
      loadUsers(1, usersSearch);
    }, 250);
    return () => window.clearTimeout(t);
  }, [usersSearch, isLoading, canAccess, loadUsers]);

  useEffect(() => {
    // Set default eval competition once competitions load
    if (!evalCompetitionId && competitions.length > 0) {
      setEvalCompetitionId(Number(competitions[0].id));
    }
  }, [competitions, evalCompetitionId]);

  const handleGeneratePairPool = async () => {
    if (!evalCompetitionId) return;
    setEvalLoading(true);
    setEvalError('');
    try {
      const token = authUtils.getStoredToken();
      const res = await apiService.post(`/evaluations/create_poolpair/${evalCompetitionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const created = res.data?.created_pairs;
      const total = res.data?.pairs_after;
      setEvalToast(`PairPool generated: created ${created ?? 0} pairs (total ${total ?? '-'})`);
    } catch (err: any) {
      console.error('Failed to generate PairPool', err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to generate PairPool';
      setEvalError(String(msg));
    } finally {
      setEvalLoading(false);
    }
  };

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(usersTotal / usersPageSize));
  }, [usersTotal, usersPageSize]);


  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!canAccess) {
    return (
     <MessagePage
      title="Access Denied"
      message="You do not have permission to view this page."
      type="error"
      showCloseButton={true}
      autoClose={8} // Auto close success after 10 seconds
    />
    )
  }

  const TITLE: Record<AdminSection, string> = {
    competitions: 'Competitions',
    evaluations: 'Evaluations',
    users: 'Users',
    agent: 'Agent',
  };

  const ACTIONS: Record<AdminSection, React.ReactNode> = {
    competitions: (
      <>
        <Button variant="ghost" size="md" onClick={loadCompetitions} disabled={competitionsLoading}>
          Refresh
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            setModalMode('create');
            setModalOpen(true);
          }}
        >
          New competition
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={() => setAddHostOpen(true)}
          disabled={competitionsLoading || competitions.length === 0}
        >
          Add host
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={() => setNotificationModalOpen(true)}
          disabled={competitionsLoading}
        >
          Send notification
        </Button>
      </>
    ),
    evaluations: null,
    agent: null,
    users: (
      <>
        <Button variant="primary" size="md" onClick={() => setAddUsersOpen(true)} disabled={usersLoading}>
          Add users
        </Button>
        <Button variant="ghost" size="md" onClick={() => loadUsers(usersPage)} disabled={usersLoading}>
          Refresh
        </Button>
      </>
    ),
  };

  return (
    <SideMenuLayout title={TITLE[section]} nav={<AdminNav active={section} />} actions={ACTIONS[section]} documentTitle={`Admin: ${TITLE[section]}`}>
      {section === 'competitions' && (
        <section className="admin-card" data-tour="admin-competitions">
          <p className="admin-help">
            Create a competition by providing a <code>path_id</code>. The UI will construct the asset URLs
            (logo/instructions/evaluation) automatically.
          </p>

          <div className="admin-actions admin-actions--spaced">
            <select
              className="input input--default input--md"
              aria-label="Competition to manage"
              value={manageCompetitionId}
              onChange={(e) => setManageCompetitionId(Number(e.target.value))}
              disabled={competitionsLoading || competitions.length === 0}
            >
              <option value={0}>Select a competition…</option>
              {competitions.map((competition) => (
                <option key={competition.id} value={competition.id}>{competition.title}</option>
              ))}
            </select>
            <Button
              variant="secondary"
              size="md"
              onClick={() => navigate(`/manage/${manageCompetitionId}?tab=content`)}
              disabled={!manageCompetitionId}
            >
              Manage competition
            </Button>
          </div>

          {competitionsError && <div className="admin-error">{competitionsError}</div>}
          {competitionsLoading && <p className="admin-status">Loading competitions...</p>}
          {!competitionsLoading && !competitionsError && competitions.length > 0 && (
            <p className="admin-status admin-status--muted">
              Loaded {competitions.length} competitions.
            </p>
          )}
        </section>
      )}

      {section === 'evaluations' && (
        <section className="admin-card" data-tour="admin-evaluations">
          <p className="admin-help">
            Tools for evaluation workflows (PairPool). PairPool is used to pre-generate unique judging pairs for
            competitions.
          </p>

          <div className="admin-actions admin-actions--spaced admin-actions--bottom">
            <label className="input-wrapper admin-eval-picker">
              <span className="input__label">Competition</span>
              <select
                className="input input--default input--md"
                value={String(evalCompetitionId || '')}
                onChange={(e) => setEvalCompetitionId(Number(e.target.value))}
                disabled={competitionsLoading || competitions.length === 0}
              >
                {competitions.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.title} (id={c.id})
                  </option>
                ))}
              </select>
            </label>

            <Button
              variant="primary"
              size="md"
              onClick={handleGeneratePairPool}
              disabled={evalLoading || !evalCompetitionId || competitions.length === 0}
            >
              Generate pairs
            </Button>

            <Button
              variant="secondary"
              size="md"
              onClick={() => setPairPoolModalOpen(true)}
              disabled={evalLoading || !evalCompetitionId || competitions.length === 0}
            >
              View pair pool
            </Button>
          </div>

          {evalError && <div className="admin-error">{evalError}</div>}
        </section>
      )}

      {section === 'users' && (
        <section className="admin-card" data-tour="admin-users">
          {usersError && <div className="admin-error">{usersError}</div>}

          <div className="admin-users-controls">
            <ListControls
              searchValue={usersSearch}
              onSearchChange={setUsersSearch}
              searchPlaceholder="Search by username or name..."
            />
          </div>

          {usersLoading ? (
            <p className="admin-status">Loading users...</p>
          ) : users.length === 0 ? (
            <p className="admin-status admin-status--muted">No users returned.</p>
          ) : (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '-'}</td>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="admin-pagination">
                <div className="admin-pagination__summary">
                  Page {usersPage} of {totalPages} • Total users: {usersTotal}
                </div>
                <div className="admin-actions">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadUsers(Math.max(1, usersPage - 1))}
                    disabled={usersLoading || usersPage <= 1}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadUsers(Math.min(totalPages, usersPage + 1))}
                    disabled={usersLoading || usersPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {section === 'agent' && <AgentSection />}

      <AdminCompetitionModal
        isOpen={modalOpen}
        mode={modalMode}
        onClose={() => setModalOpen(false)}
        competitions={competitions}
        onSuccess={loadCompetitions}
      />

      <AdminAddHostModal
        isOpen={addHostOpen}
        onClose={() => setAddHostOpen(false)}
        competitions={competitions}
        onSuccess={() => {
          // For now, just refresh lists.
          loadCompetitions();
        }}
      />

      <NotificationComposerModal
        isOpen={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        competitions={competitions}
        allowScopeSelection={true}
        title="Send Platform Notification"
      />

      <AdminPairPoolModal
        isOpen={pairPoolModalOpen}
        onClose={() => setPairPoolModalOpen(false)}
        competitionId={evalCompetitionId}
      />

      <AdminAddUsersModal
        isOpen={addUsersOpen}
        onClose={() => setAddUsersOpen(false)}
        onSuccess={() => {
          // Refresh current page after bulk create.
          loadUsers(usersPage);
        }}
      />

      {evalToast && <Toast message={evalToast} duration={3000} onClose={() => setEvalToast('')} />}
    </SideMenuLayout>
  );
};

export default Admin;
